import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormFieldsetComponent } from '../../shared/components/form-fieldset.component';
import { RouterLink } from '@angular/router';
import { OnlinePaymentSetupStepComponent } from '../components/online-payment-setup-step.component';
import {
  selectAllRooms,
  selectAuthUserLoading,
  selectAuthVendor,
  selectRoomsLoading,
} from '../../core/store/reducers';
import { Store } from '@ngrx/store';
import { roomsActions } from 'angular/app/core/store/rooms';
import { Room } from '../../core/models/entities/room';
import { BookableBy } from '../../core/models/common/bookable-by';
import { InputOption } from '../../core/models/common/input-option';
import { SharedModule } from '../../shared/shared.module';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { VendorsService } from '../../core/services/vendors.service';
import { InputOptionsHelper } from '../../core/helpers/input-options.helper';
import { Plan, PlanHumanized } from '../../core/models/common/plan';
import { DestroyableComponent } from '../../shared/components/destroyable/destroyable.component';
import { distinctUntilChanged, skipWhile, takeUntil } from 'rxjs';
import { ToastService } from '../../core/services/toast.service';
import { ApiError } from '../../core/models/common/api-error';
import {
  PaymentCaptureMethod,
  PaymentCaptureMethodHumanized,
  PayrexxInstanceStatus,
  QrInvoiceReminderDelayType,
} from '../../core/models/entities/vendor';
import { authActions } from 'angular/app/core/store/auth';
import { take } from 'rxjs/operators';
import { EnumPipe } from '../../core/pipes/enum.pipe';
import { SharedManagerModule } from '../../shared-manager/shared-manager.module';
import { SettingsModule } from '../settings.module';

@Component({
  selector: 'app-online-payment-setup-page',
  template: `
    <app-page-header title="Organisation"></app-page-header>

    <app-spacer size="4"></app-spacer>

    <app-admin-tabs></app-admin-tabs>

    <app-spacer size="6"></app-spacer>

    @if (fetchingAccountVerificationStatus()) {
      <app-loader [centered]="true"></app-loader>
    } @else {
      @if (accountVerified()) {
        <div class="border-b border-gray-200 pb-12">
          <app-fieldset>
            <div title>Configuration de Payrexx</div>
            <div description>
              Le paiement en ligne est activé.<br />
              Votre plan est :
              <app-badge variant="secondary"
                >{{ vendor()?.plan | enum: PlanHumanized }}
              </app-badge>
            </div>

            <div class="bg-light rounded-lg p-8">
              <app-online-payment-setup-step
                [completed]="accountVerified()"
                [loadingData]="fetchingAccountVerificationStatus()"
              >
                <div title>Configuration terminée</div>
                <div description>
                  <p
                    >Votre demande a été approuvée par Payrexx. Vos clients
                    peuvent maintenant effectuer les paiements en ligne.
                  </p>
                  <div class="my-6 h-px bg-gray-200"></div>
                  <app-toggle
                    title="Paiement en ligne avec Payrexx"
                    [defaultValue]="!vendor().needs_to_complete_payment_setup"
                    [disabled]="
                      !vendor().needs_to_complete_payment_setup ||
                      creatingPayrexxInstance()
                    "
                  ></app-toggle>
                </div>
              </app-online-payment-setup-step>
            </div>
          </app-fieldset>
        </div>

        <div class="border-b border-gray-200 py-12">
          <app-fieldset>
            <div title>Prélèvement du montant</div>
            <div description
              ><p
                >Vous pouvez choisir si le client est débité dès sa demande de
                réservation, ou uniquement lorsque celle-ci est confirmée par un
                admin.</p
              >
              <br />
              <p
                >En cas de débit lors de la confirmation de réservation, le
                client renseigne ses informations de paiement lors du processus
                de demande de réservation.</p
              ></div
            >

            <div class="bg-light flex items-center gap-6 rounded-lg p-8">
              <app-radio-input
                name="payment_capture_method"
                [control]="form.get('payment_capture_method')"
                variant="native"
                [inline]="true"
                [options]="paymentCaptureMethodOptions"
                [marginBottom]="false"
              ></app-radio-input>
              @if (updatingPaymentCaptureMethod()) {
                <div class="mt-2">
                  <app-loader size="sm"></app-loader>
                </div>
              }
            </div>
          </app-fieldset>
        </div>

        <div class="border-b border-gray-200 py-12">
          <app-fieldset>
            <div title>Paiement sur facture</div>
            <div description
              ><p
                >Vous pouvez choisir d’activer le paiement sur facture ou non
                lorsque le client réserve une salle.
              </p>
              <br />
              <p
                >Un montant minimal peut-être exigé pour permettre à vos clients
                de payer sur facture.</p
              ></div
            >

            <div class="bg-light rounded-lg p-8">
              <app-single-checkbox-input
                name="qr_invoice_enabled"
                [control]="form.get('qr_invoice_enabled')"
                label="Paiement sur facture"
              ></app-single-checkbox-input>
              <div class="mt-4">
                @if (form.get('qr_invoice_enabled').value) {
                  <div class="grid grid-cols-1 gap-6 md:grid-cols-4">
                    <div class="md:col-span-2">
                      <app-text-input
                        label="Autoriser le paiement sur facture dès"
                        prepend="CHF"
                        name="min_amount"
                        helpText="Si aucun montant minimal n'est exigé, entrez 0."
                        [control]="form.get('qr_invoice_settings.min_amount')"
                      ></app-text-input>
                    </div>

                    <div class="md:col-span-2">
                      <app-select-input
                        label="Date d'échéance des factures"
                        helpText="La date d'échéance est calculée à partir de la date de création de la facture, pour vos clients qui choisissent de payer sur facture."
                        name="due_after_days"
                        [control]="
                          form.get('qr_invoice_settings.due_after_days')
                        "
                        [options]="[
                          { value: 5, label: '5 jours' },
                          { value: 10, label: '10 jours' },
                          { value: 20, label: '20 jours' },
                          { value: 30, label: '30 jours' },
                          { value: 40, label: '40 jours' },
                          { value: 50, label: '50 jours' },
                          { value: 60, label: '60 jours' },
                        ]"
                      ></app-select-input>
                    </div>

                    @if (remindersForm) {
                      <div class="md:col-span-3">
                        <app-text-input
                          label="Envoyer un rappel en cas de non-paiement"
                          name="reminders_0_days"
                          helpText="Payrexx envoie ce rappel par e-mail automatiquement."
                          [control]="remindersForm.at(0).get('days')"
                          [appendOptions]="[
                            {
                              label: 'jours avant la date d’échéance',
                              value: 'days_before_due_date',
                            },
                            {
                              label: 'jours après la date d’échéance',
                              value: 'days_after_due_date',
                            },
                            {
                              label: 'jours après la création',
                              value: 'days_after_invoice_date',
                            },
                          ]"
                          [appendOptionValue]="
                            remindersForm.at(0).value.delay_type
                          "
                          (appendOptionValueChange)="
                            remindersForm
                              .at(0)
                              .controls.delay_type.patchValue($any($event))
                          "
                        ></app-text-input>
                      </div>
                    }
                  </div>
                }

                <app-button
                  [loading]="updatingQrInvoiceMinAmount()"
                  (click)="updateQrSettings()"
                  variant="secondary"
                  size="sm"
                  icon="check"
                  >Enregistrer
                </app-button>
              </div>
            </div>
          </app-fieldset>
        </div>
      } @else {
        <div class="border-b border-gray-200 pb-12">
          <app-fieldset>
            <div title>Étape 1 : Préparation des données</div>
            <div description
              >Afin d'être éligible au paiement en ligne, vous devez suivre
              plusieurs étapes. Ces points seront vérifiés par notre partenaire
              et fournisseur de paiement en ligne Payrexx.
            </div>

            <div class="bg-light rounded-lg p-8">
              <app-online-payment-setup-step
                [completed]="vendorDataCompleted()"
              >
                <div title>Données administratives</div>
                <div description>
                  <p
                    >Dans l’onglet
                    <a routerLink="/administration/donnees"
                      >Données administratives</a
                    >, remplissez vos coordonnées ainsi que le document
                    concernant les conditions générales.</p
                  >
                </div>
              </app-online-payment-setup-step>

              <div class="my-8 h-px bg-gray-100"></div>

              <app-online-payment-setup-step
                [completed]="atLeastOnePublicActiveRoom()"
                [loadingData]="loadingRooms()"
              >
                <div title>Activez au moins une salle publique</div>
                <div description>
                  <p
                    >Dans le menu <a routerLink="/salles">Salles</a>, activez
                    une salle et vérifiez que celle-ci est ouverte à la location
                    publique (voir "Qui peut louer la salle" ?).</p
                  >
                </div>
              </app-online-payment-setup-step>
            </div>
          </app-fieldset>
        </div>

        <div class="border-b border-gray-200 py-12">
          <app-fieldset>
            <div title>Étape 2 : Choisir votre plan d'abonnement</div>
            <div description
              >Souscrire un plan d’abonnement payant est obligatoire lorsque
              vous souhaitez activer le paiement en ligne. Vous pouvez utiliser
              <a href="/tarifs" target="_blank">notre calculateur</a> afin de
              comparer nos deux types d’abonnements : annuel ou à la commission.
            </div>

            <div class="bg-light rounded-lg p-8">
              <app-online-payment-setup-step
                [completed]="
                  vendor()?.plan === Plan.AnnualSubscription ||
                  vendor()?.plan === Plan.CommissionBased
                "
                [loadingData]="!vendor()?.plan || updatingPlan()"
              >
                <div title>Choisir un abonnement</div>
                <div description>
                  Choisissez ci-dessous le plan d'abonnement auquel vous
                  souhaitez souscrire.<br /><a href="/tarifs" target="_blank"
                    >Voir calculateur</a
                  >
                  <i class="fal fa-external-link ml-2"></i>

                  <div class="mt-8">
                    <app-radio-input
                      name="plan"
                      [control]="form.get('plan')"
                      variant="native"
                      [inline]="true"
                      [options]="plansOptions"
                      [marginBottom]="false"
                    ></app-radio-input>
                  </div>
                </div>
              </app-online-payment-setup-step>
            </div>
          </app-fieldset>
        </div>

        <div class="border-b border-gray-200 py-12">
          <app-fieldset>
            <div title>Étape 3 : Configuration Payrexx</div>
            <div description>
              <p
                ><a href="https://payrexx.com/fr-ch" target="_blank">Payrexx</a>
                est notre partenaire pour le paiement en ligne. Une fois les
                étapes précédentes remplies, vous pourrez activer “Paiement en
                ligne”.</p
              >
              <p
                >Vous allez être redirigé·e vers le portail Payrexx afin de
                remplir le formulaire de vérification et soumettre votre
                demande.</p
              >
            </div>

            <div class="bg-light rounded-lg p-8">
              <app-online-payment-setup-step
                [completed]="!vendor()?.needs_to_complete_payment_setup"
              >
                <div title>Créez votre compte Payrexx</div>
                <div description>
                  @if (vendor()?.needs_to_complete_payment_setup) {
                    <p
                      >Nous allons créer un compte Payrexx en votre nom lorsque
                      vous cliquez sur "Créer un compte Payrexx".<br />Une fois
                      le compte créé, vous recevrez un e-mail afin des
                      instructions pour y accéder.<br /><br />
                      <strong
                        ><i class="far fa-exclamation-triangle mr-1.5"></i> Le
                        processus de création prend plusieurs dizaines de
                        secondes.</strong
                      ></p
                    >

                    <div class="mt-6">
                      <app-button
                        size="sm"
                        variant="secondary"
                        [loading]="creatingPayrexxInstance()"
                        (click)="setupPayrexxInstance()"
                        >Créer un compte Payrexx
                      </app-button>
                    </div>
                  } @else {
                    Votre compte Payrexx a été créé.<br />Consultez votre boite
                    e-mail qui contient un lien pour vous connecter.<br />
                    Si vous n'avez pas reçu d'e-mail, vous pouvez réinitialiser
                    votre mot de passe sur la
                    <a
                      href="https://login.pay.location-salles.ch/fr"
                      target="_blank"
                      >page de connexion de Payrexx</a
                    >
                    à l'aide du lien "Mot de passe oublié".
                  }
                </div>
              </app-online-payment-setup-step>

              @if (
                !vendor()?.needs_to_complete_payment_setup && !accountVerified()
              ) {
                <div class="my-8 h-px bg-gray-100"></div>

                <app-online-payment-setup-step
                  [completed]="false"
                  [customIconWithColor]="
                    accountVerificationStatus() === PayrexxInstanceStatus.Review
                      ? 'fa-hourglass-clock text-orange-500'
                      : undefined
                  "
                >
                  <div title>Soumettez votre demande auprès de Payrexx</div>
                  <div description>
                    @if (
                      accountVerificationStatus() ===
                      PayrexxInstanceStatus.Review
                    ) {
                      Votre demande est en cours de traitement par Payrexx.<br />Vous
                      recevrez un e-mail dès que votre compte sera approuvé.
                    } @else {
                      Rendez-vous dans votre
                      <a
                        href="https://login.pay.location-salles.ch/fr"
                        target="_blank"
                        >espace Payrexx</a
                      >
                      (menu "Vérifier le compte") et remplissez le formulaire
                      afin de pouvoir accepter les paiements en ligne au nom de
                      votre organisation.
                    }
                  </div>
                </app-online-payment-setup-step>
              }
            </div>
          </app-fieldset>
        </div>
      }
    }
  `,
  imports: [
    FormFieldsetComponent,
    RouterLink,
    OnlinePaymentSetupStepComponent,
    SharedModule,
    EnumPipe,
    SharedManagerModule,
    SettingsModule,
  ],
})
export class OnlinePaymentSetupPageComponent extends DestroyableComponent {
  protected readonly Plan = Plan;
  store = inject(Store);
  vendor = this.store.selectSignal(selectAuthVendor);
  form: FormGroup;
  plansOptions: InputOption[] = InputOptionsHelper.fromEnum(
    Plan,
    PlanHumanized,
  ).filter((option: InputOption) => {
    return option.value !== Plan.Free;
  });
  paymentCaptureMethodOptions: InputOption[] = InputOptionsHelper.fromEnum(
    PaymentCaptureMethod,
    PaymentCaptureMethodHumanized,
  );
  vendorsService = inject(VendorsService);
  toastService = inject(ToastService);
  authUserLoading$ = this.store.select(selectAuthUserLoading);
  loadingRooms = this.store.selectSignal(selectRoomsLoading);
  updatingPlan = signal<boolean>(false);
  updatingPaymentCaptureMethod = signal<boolean>(false);
  updatingQrInvoiceMinAmount = signal<boolean>(false);
  creatingPayrexxInstance = signal<boolean>(false);
  accountVerificationStatus = signal<PayrexxInstanceStatus | null>(null);
  accountVerified = computed(
    () => this.accountVerificationStatus() === PayrexxInstanceStatus.Approved,
  );
  fetchingAccountVerificationStatus = signal<boolean>(false);

  vendorDataCompleted = computed<boolean>(() => {
    return (
      !!this.vendor()?.address &&
      !!this.vendor()?.zip &&
      !!this.vendor()?.city &&
      !!this.vendor()?.email &&
      !!this.vendor()?.phone &&
      !!this.vendor()?.general_terms_public_url
    );
  });

  atLeastOnePublicActiveRoom = computed<boolean>(() => {
    const rooms = this.store.selectSignal(selectAllRooms);
    return rooms()?.some((room: Room) => {
      return room.bookable_by !== BookableBy.Private && room.active;
    });
  });

  get remindersForm(): FormArray<
    FormGroup<{
      days: FormControl<number>;
      fee: FormControl<number>;
      delay_type: FormControl<QrInvoiceReminderDelayType>;
    }>
  > {
    return this.form?.get('qr_invoice_settings.reminders') as FormArray;
  }

  constructor() {
    super();

    this.store.dispatch(roomsActions.findAll({}));
    const vendorReminders = this.vendor()?.qr_invoice_settings?.reminders;
    this.form = new FormGroup({
      plan: new FormControl(this.vendor()?.plan),
      payment_capture_method: new FormControl(
        this.vendor()?.payment_capture_method,
      ),
      qr_invoice_enabled: new FormControl<boolean>(
        !!this.vendor()?.qr_invoice_enabled,
      ),
      qr_invoice_settings: new FormGroup({
        min_amount: new FormControl(
          this.vendor()?.qr_invoice_settings?.min_amount ?? 0,
          [Validators.min(0)],
        ),
        due_after_days: new FormControl(
          this.vendor()?.qr_invoice_settings?.due_after_days ?? 30,
        ),
        reminders: new FormArray([
          new FormGroup({
            days: new FormControl<number>(
              vendorReminders?.length > 0 ? vendorReminders[0].days : 0,
            ),
            fee: new FormControl<number>(0),
            delay_type: new FormControl<QrInvoiceReminderDelayType>(
              vendorReminders?.length > 0
                ? vendorReminders[0].delay_type
                : QrInvoiceReminderDelayType.DaysAfterDueDate,
            ),
          }),
        ]),
      }),
    });

    effect(() => {
      if (this.vendor()) {
        // après chargement du vendor (si on arrive sur la page avec un refresh)
        this.form
          .get('payment_capture_method')
          .setValue(this.vendor().payment_capture_method, { emitEvent: false });
      }
    });
  }

  ngOnInit() {
    if (!this.vendor()?.needs_to_complete_payment_setup) {
      // Une fois le compte créé, on peut vérifier son statut
      this.fetchingAccountVerificationStatus.set(true);
      this.vendorsService
        .findPayrexxVerificationStatus()
        .then((response) => {
          this.accountVerificationStatus.set(response.data.status);
          this.fetchingAccountVerificationStatus.set(false);
        })
        .catch(() => {
          this.fetchingAccountVerificationStatus.set(false);
        });
    }

    this.form
      .get('plan')
      .valueChanges.pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe(async (plan: Plan) => {
        this.updatingPlan.set(true);
        try {
          await this.vendorsService.update({
            name: this.vendor().name,
            slug: this.vendor().slug,
            plan: plan,
          });
          this.store.dispatch(authActions.findCurrentUser());
          this.authUserLoading$
            .pipe(
              skipWhile((loading) => loading === true),
              take(1),
            )
            .subscribe(() => {
              this.updatingPlan.set(false);
            });
        } catch (exception) {
          this.toastService.error((exception as ApiError).message);
          this.form
            .get('plan')
            .setValue(this.vendor()?.plan, { emitEvent: false });
          this.updatingPlan.set(false);
        }
      });

    this.form
      .get('payment_capture_method')
      .valueChanges.pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe(async (paymentCaptureMethod: PaymentCaptureMethod) => {
        this.updatingPaymentCaptureMethod.set(true);
        try {
          await this.vendorsService.update({
            name: this.vendor().name,
            slug: this.vendor().slug,
            payment_capture_method: paymentCaptureMethod,
          });
          this.store.dispatch(authActions.findCurrentUser());
          this.toastService.success('Modifications enregistrées.');
        } catch (exception) {
          this.toastService.error((exception as ApiError).message);
          this.form
            .get('payment_capture_method')
            .setValue(this.vendor()?.payment_capture_method, {
              emitEvent: false,
            });
        } finally {
          this.updatingPaymentCaptureMethod.set(false);
        }
      });

    this.form
      .get('qr_invoice_enabled')
      .valueChanges.pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe((qrInvoiceEnabled) => {
        if (qrInvoiceEnabled) {
          this.form
            .get('qr_invoice_settings.min_amount')
            .setValidators([Validators.required]);
          this.form
            .get('qr_invoice_settings.due_after_days')
            .setValidators([Validators.required]);
        } else {
          this.form.get('qr_invoice_settings.min_amount').clearValidators();
          this.form.get('qr_invoice_settings.due_after_days').clearValidators();
        }
        this.form
          .get('qr_invoice_settings.min_amount')
          .updateValueAndValidity();
        this.form
          .get('qr_invoice_settings.due_after_days')
          .updateValueAndValidity();
      });
  }

  async updateQrSettings() {
    this.updatingQrInvoiceMinAmount.set(true);
    try {
      await this.vendorsService.update({
        name: this.vendor().name,
        slug: this.vendor().slug,
        qr_invoice_enabled: this.form.value.qr_invoice_enabled,
        qr_invoice_settings: this.form.value.qr_invoice_settings,
      });
      this.store.dispatch(authActions.findCurrentUser());
      this.toastService.success('Modifications enregistrées.');
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
      this.form
        .get('qr_invoice_settings')
        .setValue(this.vendor()?.qr_invoice_settings, {
          emitEvent: false,
        });
    } finally {
      this.updatingQrInvoiceMinAmount.set(false);
    }
  }

  async setupPayrexxInstance() {
    try {
      this.creatingPayrexxInstance.set(true);
      await this.vendorsService.createPayrexxMerchantInstance();
      this.store.dispatch(authActions.findCurrentUser());
      this.toastService.success(
        'Votre compte a bien été créé. Veuillez suivre les instructions reçues par e-mail pour y accéder et soumettre votre demande.',
      );
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
    } finally {
      this.creatingPayrexxInstance.set(false);
    }
  }

  protected readonly PlanHumanized = PlanHumanized;
  protected readonly PayrexxInstanceStatus = PayrexxInstanceStatus;
}
